import { useParams, useLocation, Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const params = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const [tocActive, setTocActive] = useState('');

  const lang = useMemo(() => {
    const pathLang = location.pathname.split('/')[1];
    if (['en', 'fr', 'de', 'da'].includes(pathLang)) return pathLang;
    return 'en';
  }, [location.pathname]);

  const slug = params.slug || '';
  const article = articles[slug];

  useEffect(() => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!article) return;
    const headings = document.querySelectorAll('article h2[id]');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTocActive(entry.target.id);
          }
        });
      },
      { rootMargin: '-80px 0px -70% 0px' }
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar lang={lang} />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <Link to={`/${lang}`} className="text-primary-600 hover:text-primary-700 font-medium">
              Return to Home
            </Link>
          </div>
        </main>
        <Footer lang={lang} />
      </div>
    );
  }

  const homePath = `/${lang}`;

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar lang={lang} />
      <main className="flex-1">
        {/* Article Hero */}
        <section className="w-full relative overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black/60" />
          </div>
          <div className="relative z-10 section-container py-16 md:py-24">
            <div className="section-inner max-w-4xl">
              {/* Breadcrumb */}
              <nav className="flex items-center gap-2 text-sm text-white/70 mb-6">
                <Link to={homePath} className="hover:text-white transition-colors">
                  {t('article_breadcrumb_home')}
                </Link>
                <i className="ri-arrow-right-s-line text-xs" />
                <span className="text-white/90">{t('article_breadcrumb_article')}</span>
              </nav>

              <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 font-heading">
                {article.title}
              </h1>
              <p className="text-base md:text-lg text-white/80 leading-relaxed mb-6">
                {article.excerpt}
              </p>
              <div className="flex items-center gap-4 text-sm text-white/70">
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line" />
                  {article.readTime} {t('article_read_time')}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-calendar-line" />
                  {t('article_updated')}: {article.updatedDate}
                </span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-user-line" />
                  {t('article_author')}
                </span>
              </div>
            </div>
          </div>
        </section>

        <div className="section-container py-10 md:py-16">
          <div className="section-inner max-w-6xl">
            <div className="grid lg:grid-cols-[1fr_280px] gap-8 lg:gap-12">
              {/* Main content */}
              <article className="prose-custom">
                {/* Intro */}
                <div className="mb-10">
                  <h2 id="intro" className="text-lg font-bold text-foreground-900 mb-4 font-heading">
                    {t('article_intro')}
                  </h2>
                  <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                    {article.intro}
                  </p>
                </div>

                {/* Sections */}
                {article.sections.map((section, idx) => (
                  <div key={idx} className="mb-10">
                    <h2
                      id={`section-${idx}`}
                      className="text-xl md:text-2xl font-bold text-foreground-950 mb-4 font-heading"
                    >
                      {section.h2}
                    </h2>
                    {section.content && (
                      <p className="text-base text-foreground-700 leading-relaxed mb-4">
                        {section.content}
                      </p>
                    )}
                    {section.h3s?.map((sub, sIdx) => (
                      <div key={sIdx} className="mb-5">
                        <h3 className="text-lg font-semibold text-foreground-900 mb-2 font-heading">
                          {sub.h3}
                        </h3>
                        <p className="text-base text-foreground-700 leading-relaxed">
                          {sub.content}
                        </p>
                      </div>
                    ))}
                  </div>
                ))}

                {/* Conclusion */}
                <div className="mb-10 p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70">
                  <h2 id="conclusion" className="text-lg font-bold text-foreground-900 mb-4 font-heading">
                    {t('article_conclusion')}
                  </h2>
                  <p className="text-base text-foreground-700 leading-relaxed">
                    {article.conclusion}
                  </p>
                </div>

                {/* CTA block */}
                <div className="mb-10 p-6 md:p-8 rounded-lg bg-primary-50 border border-primary-200/60 text-center">
                  <p className="text-base text-foreground-700 mb-4">
                    {t('article_cta_text')}
                  </p>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    {t('article_cta_button')}
                    <i className="ri-arrow-right-line" />
                  </a>
                </div>

                {/* Back to home link */}
                <div className="mb-6">
                  <Link
                    to={homePath}
                    className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    <i className="ri-arrow-left-line" />
                    {t('article_back_home')}
                  </Link>
                </div>
              </article>

              {/* Sidebar */}
              <aside className="hidden lg:block">
                <div className="sticky top-24 space-y-6">
                  {/* TOC */}
                  <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                    <h3 className="font-semibold text-foreground-950 mb-4 text-sm uppercase tracking-wide">
                      {t('article_toc')}
                    </h3>
                    <nav className="space-y-2">
                      <a
                        href="#intro"
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById('intro');
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className={`block text-sm transition-colors cursor-pointer ${
                          tocActive === 'intro' ? 'text-primary-600 font-medium' : 'text-foreground-600 hover:text-primary-600'
                        }`}
                      >
                        {t('article_intro')}
                      </a>
                      {article.sections.map((s, i) => (
                        <a
                          key={i}
                          href={`#section-${i}`}
                          onClick={(e) => {
                            e.preventDefault();
                            const el = document.getElementById(`section-${i}`);
                            if (el) el.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className={`block text-sm transition-colors cursor-pointer ${
                            tocActive === `section-${i}` ? 'text-primary-600 font-medium' : 'text-foreground-600 hover:text-primary-600'
                          }`}
                        >
                          {s.h2}
                        </a>
                      ))}
                      <a
                        href="#conclusion"
                        onClick={(e) => {
                          e.preventDefault();
                          const el = document.getElementById('conclusion');
                          if (el) el.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className={`block text-sm transition-colors cursor-pointer ${
                          tocActive === 'conclusion' ? 'text-primary-600 font-medium' : 'text-foreground-600 hover:text-primary-600'
                        }`}
                      >
                        {t('article_conclusion')}
                      </a>
                    </nav>
                  </div>

                  {/* Related */}
                  <div className="p-5 rounded-lg bg-background-100 border border-background-200/70">
                    <h3 className="font-semibold text-foreground-950 mb-4 text-sm uppercase tracking-wide">
                      {t('article_related')}
                    </h3>
                    <div className="space-y-3">
                      {article.relatedSlugs.map((rSlug) => {
                        const related = articles[rSlug];
                        if (!related) return null;
                        return (
                          <Link
                            key={rSlug}
                            to={`/${lang}/article/${rSlug}`}
                            className="block p-3 rounded-md bg-background-50 border border-background-200/40 hover:border-primary-200 hover:bg-primary-50/30 transition-all"
                          >
                            <p className="text-sm font-medium text-foreground-800 hover:text-primary-700 transition-colors">
                              {related.title}
                            </p>
                            <p className="text-xs text-foreground-500 mt-1">
                              {related.readTime} {t('article_read_time')}
                            </p>
                          </Link>
                        );
                      })}
                    </div>
                  </div>

                  {/* External CTA */}
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="block p-5 rounded-lg bg-primary-500 text-white text-center hover:bg-primary-600 transition-colors"
                  >
                    <p className="font-semibold mb-1">{t('ext_btn_main')}</p>
                    <p className="text-sm text-white/80">kaspreadsheet.com</p>
                  </a>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </main>
      <Footer lang={lang} />
    </div>
  );
}